import React, { useCallback, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronRight } from "@piiqtechnologies/svg-icons";
import { PeekBox, PeekList, PeekListItem, PeekListItemButton, PeekListItemText, PeekStack, PeekTooltip, PeekTypography, useTheme, } from "@piiqtechnologies/ui-components";
import { AnalyticsArea, useTrackEvent } from "@hooks";
import { useOrgPath } from "@stores/ActiveOrganizationContext";
import { reportPath, topicsAndSourcesPath } from "@utils/orgPath";
import messages from "../messages.i18n";
export const CommunityAccordionItem = ({ communityId, name, reportId, isExpanded, isActive, onOpen, }) => {
    const intl = useIntl();
    const theme = useTheme();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const orgPath = useOrgPath();
    const track = useTrackEvent();
    const activeColor = theme.palette.teal[600];
    // Community names are cut off by the sidebar's width, and the cut name is often the only thing
    // telling two buildings apart. Measured on hover rather than on render: the row is what knows
    // its own width, and a name that fits needs no tooltip at all.
    const nameRef = useRef(null);
    const [isNameTruncated, setIsNameTruncated] = useState(false);
    const measureName = useCallback(() => {
        const element = nameRef.current;
        if (element)
            setIsNameTruncated(element.scrollWidth > element.clientWidth);
    }, []);
    const subPages = reportId
        ? [
            { key: "report", label: intl.formatMessage(messages.communityReport), to: orgPath(reportPath(reportId)) },
            {
                key: "topicsAndSources",
                label: intl.formatMessage(messages.communityTopicsAndSources),
                to: orgPath(topicsAndSourcesPath(reportId)),
            },
        ]
        : [];
    const highlighted = isActive;
    const rowRef = useRef(null);
    useEffect(() => {
        var _a, _b;
        if (highlighted)
            (_b = (_a = rowRef.current) === null || _a === void 0 ? void 0 : _a.scrollIntoView) === null || _b === void 0 ? void 0 : _b.call(_a, { block: "nearest" });
    }, [highlighted]);
    return (React.createElement(PeekBox, { component: "li", ref: rowRef, sx: { listStyle: "none", "& + &": { mt: "10px" } } },
        React.createElement(PeekBox, null,
            React.createElement(PeekListItemButton, { onClick: () => onOpen(communityId), "aria-expanded": isExpanded, "aria-current": highlighted ? "true" : undefined, sx: {
                    backgroundColor: highlighted ? theme.palette.teal[100] : "initial",
                    borderLeft: `2px solid ${highlighted ? activeColor : "transparent"}`,
                    minHeight: "30px",
                    height: "30px",
                    px: 1,
                    gap: 0.5,
                } },
                React.createElement(ChevronRight, { width: 12, height: 12, style: {
                        flexShrink: 0,
                        color: highlighted ? activeColor : "inherit",
                        transform: isExpanded ? "rotate(90deg)" : "none",
                        transition: theme.transitions.create("transform", { duration: theme.transitions.duration.shortest }),
                    } }),
                React.createElement(PeekTooltip, { title: isNameTruncated ? name : "", placement: "right" },
                    React.createElement(PeekListItemText, { primary: name, onMouseEnter: measureName, onFocus: measureName, primaryTypographyProps: {
                            ref: nameRef,
                            variant: "h4",
                            noWrap: true,
                            color: highlighted ? activeColor : undefined,
                        }, sx: { minWidth: 0, my: 0 } })))),
        isExpanded && (React.createElement(PeekList, { disablePadding: true, sx: {
                display: "flex",
                flexDirection: "column",
                gap: "10px",
                ml: 1.5,
                mt: "10px",
                position: "relative",
                "&::before": {
                    content: '""',
                    position: "absolute",
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: "2px",
                    backgroundColor: theme.palette.grey[100],
                },
            } },
            !reportId && (React.createElement(PeekBox, { component: "li", sx: { listStyle: "none" } },
                React.createElement(PeekTypography, { variant: "body1", color: theme.palette.grey[300], sx: { px: 1, pl: 1.5 } }, intl.formatMessage(messages.communityNoReports)))),
            subPages.map((page) => {
                const isCurrent = pathname === page.to;
                return (React.createElement(PeekListItem, { key: page.key, disablePadding: true, sx: {
                        borderLeft: `2px solid ${isCurrent ? activeColor : "transparent"}`,
                    } },
                    React.createElement(PeekListItemButton
                    // /overview and /results carry no community, so the pages behind them would
                    // have to resolve one before the header could ask for its reports. Handing it
                    // over here removes that gap for every navigation from the sidebar.
                    , { 
                        // /overview and /results carry no community, so the pages behind them would
                        // have to resolve one before the header could ask for its reports. Handing it
                        // over here removes that gap for every navigation from the sidebar.
                        onClick: () => {
                            track(AnalyticsArea.Navigation, "Report Page Opened", { subjectId: communityId, page: page.key });
                            navigate(page.to, { state: { communityId } });
                        }, "aria-current": isCurrent ? "page" : undefined, sx: {
                            px: 1,
                            minHeight: "30px",
                            height: "30px",
                            backgroundColor: isCurrent ? theme.palette.teal[100] : "initial",
                        } },
                        React.createElement(PeekStack, { direction: "row", alignItems: "center", gap: 1, sx: { minWidth: 0 } },
                            React.createElement(PeekBox, { sx: {
                                    width: 4,
                                    height: 4,
                                    flexShrink: 0,
                                    borderRadius: "50%",
                                    backgroundColor: isCurrent ? activeColor : theme.palette.text.primary,
                                } }),
                            React.createElement(PeekListItemText, { primary: page.label, primaryTypographyProps: {
                                    variant: "h4",
                                    noWrap: true,
                                    color: isCurrent ? activeColor : undefined,
                                }, sx: { minWidth: 0, my: 0 } })))));
            })))));
};
export default CommunityAccordionItem;
